import type { ChatMode } from '../components/chat/ActiveConversationContext';

export interface NavTarget {
  href: { pathname: string; query: Record<string, string> };
  as: string;
}

// The home route ('/') is the chat's OWN surface, so a navigate there collapses the chat to its
// bubble and lets the home screen show — unlike the other screens, which "navigate and shrink" the
// chat to a floating panel beside the page.
export function isHomeTarget(desktopPath: string | undefined): boolean {
  return desktopPath === '/';
}

/**
 * A step that opens OVER the surface the user is already on, rather than a screen of its own: the
 * pathname is home and the catalog entry carries its own params (phone verification is `/?verify=phone`).
 *
 * The chat must keep whatever size it had. The other targets are real navigations, so resizing the chat
 * to sit beside the new screen is the point; here there IS no new screen — the step opens on top of the
 * conversation it was offered in, and shrinking that conversation to a side dock (or a bubble) is a
 * change to the thing the user was reading that they did not ask for.
 */
export function isOverlayTarget(desktopPath: string | undefined): boolean {
  if (!desktopPath) return false;
  const [pathname, query] = desktopPath.split('?');
  return pathname === '/' && Boolean(query);
}

/**
 * Build a Next route + brand-mounted display path from a ClientControl navigate command's resolved
 * `link.desktop` — a basePath-relative path the navigation catalog produced ('/', '/tasks',
 * '/pages/<slug>'); basePath carries /orion-web or /symphony-web. A `/pages/<slug>` path maps to the
 * dynamic `/pages/[slug]` route; everything else is a static route.
 *
 * The chat follows in the URL — its thread as `?conversation=<id>` and its SIZE as `?chat=<mode>`.
 * Carrying the size matters: the URL is the durable description of the visible chat (FloatingChat
 * restores from it on mount), so a remount / refresh / the home route's getServerSideProps round-trip
 * resolves the size we INTENDED rather than the route's open-intent default — which on `/` is
 * `expanded`, the wrong size for a "navigate home and get out of the way". Without `?chat`, a
 * navigate-to-home re-resolves to expanded and clobbers the bubble we set in memory.
 */
export function clientControlTarget(
  desktopPath: string,
  conversationId: string,
  basePath: string,
  chatMode: ChatMode,
): NavTarget {
  // A catalog entry may carry its own params — phone verification is `/?verify=phone`, a step ON the
  // home route rather than a route of its own. So split them off: the pathname must stay a pathname
  // (Next resolves the route from it) and the params merge with the chat's, or the two `?` collide
  // into a path no route matches.
  const [pathname, ownQuery] = desktopPath.split('?');
  const own = Object.fromEntries(new URLSearchParams(ownQuery ?? ''));
  const query = { ...own, conversation: conversationId, chat: chatMode };
  const search = `?${new URLSearchParams(query).toString()}`;
  const pageMatch = pathname.match(/^\/pages\/(.+)$/);
  if (pageMatch) {
    const slug = pageMatch[1];
    return {
      href: { pathname: '/pages/[slug]', query: { slug, ...query } },
      as: `${basePath}/pages/${encodeURIComponent(slug)}${search}`,
    };
  }
  return {
    href: { pathname, query },
    as: `${basePath}${pathname}${search}`,
  };
}
